import Editor from '@ethsign/ckeditor5-build-classic';
import { MessageTextSquare02 } from '@ethsign/icons';
import { uniqBy } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

import { dragImage } from '@/constants/editor';

import { IField, InputType } from '@/types';
import { handleHtml } from '@/utils';

// eslint-disable-next-line no-duplicate-imports
import type HtmlEditorType from '@ethsign/ckeditor5-build-classic';

import './index.css';

type Props = {
  initData?: string;
  onChange?: (data: string, fields: any[]) => void;
  isDragging?: boolean;
  onSelectionChange?: (info?: IField) => void;
  type?: 'edit' | 'preview';
};
const getOffsetTop = (el: any): number => {
  let offsetTop = el.offsetTop;
  if (el.offsetParent != null && el.offsetParent.className !== 'editor-container') {
    offsetTop += getOffsetTop(el.offsetParent);
  }
  return offsetTop;
};
const handElements = (editor: any, filter: (info: IField) => boolean, cb: (writer: any, elements: any) => void) => {
  editor.model.change((writer: any) => {
    const range = editor.model.createRangeIn(editor.model.document.getRoot()!);
    const items: any = [];
    for (const item of range.getItems()) {
      if (item.name === 'signaturecomponent' && item.hasAttribute('info')) {
        const info: string = item.getAttribute('info') as string;
        const infoObj = info && JSON.parse(info);
        filter(infoObj) && items.push({ item, info: infoObj });
      }
    }
    cb(writer, items);
  });
};
export const HtmlEditor = forwardRef(function HtmlEditor(props: Props, ref) {
  const { initData, onChange, onSelectionChange, type } = props;
  const editorRef = useRef<any>();
  const [editor, setEditor] = useState<HtmlEditorType>();

  useImperativeHandle(
    ref,
    () => ({
      editor,
      focus: () => editor?.focus(),
      getData: () => handleHtml(editor?.getData() || ''),
      setData: (data: string) => editor?.setData(data),
      selectElement: (id: string) => {
        handElements(
          editor,
          (info: any) => {
            return info.id === id;
          },
          (writer, elements) => {
            for (const element of elements) {
              editor?.focus();
              writer.setSelection(element.item, 'in', { backward: true });
            }
          }
        );

        const element: any = document.getElementById(id);
        if (element) {
          const container = document.getElementsByClassName('editor-container')[0];
          container.scrollTo({ left: 0, top: getOffsetTop(element) - 200, behavior: 'smooth' });
          element.click();
        } else {
          onSelectionChange && onSelectionChange(undefined);
        }
      },
      deleteElementsBySigner: (signerId: string) => {
        if (editor) {
          handElements(
            editor,
            (info: any) => {
              if (info.custom && info.custom !== 'undefined') {
                info.custom = JSON.parse(info.custom);
              }
              return info?.custom?.id === signerId;
            },
            (writer, elements) => {
              for (const element of elements) {
                writer.remove(element.item);
              }
            }
          );
        }
      },
      deleteNotTermElement: () => {
        if (editor) {
          handElements(
            editor,
            (info) => info && info.type !== 'term',
            (writer, elements) => {
              for (const element of elements) {
                writer.remove(element.item);
              }
            }
          );
        }
      },
      modifyElement: (data: IField) => {
        if (editor) {
          handElements(
            editor,
            (info) => info && info.id === data.id,
            (writer, elements) => {
              for (const element of elements) {
                writer.rename(element.item, 'signaturecomponent');
                writer.setAttribute('info', JSON.stringify({ ...element.info, ...data }), element.item);
              }
            }
          );
        }
      }
    }),
    [editor]
  );
  useEffect(() => {
    if (editor) {
      editor.setData(initData || '');
    }
  }, [editor, initData]);
  useEffect(() => {
    if (editor) return;

    Editor.create(editorRef.current, {
      list: { properties: { styles: false, startIndex: false, reversed: false } },
      signatureComponent: {
        renderer: (info: string, domElement: any) => {
          const root = createRoot(domElement);
          const infoObj = info && JSON.parse(info);
          const { type, id, label, color, value, inputType } = infoObj;
          const isTermWithInfo = type === 'term' && (label || value);
          const isTextWithLabel = type === 'text' && label;
          let style = {};
          if (isTextWithLabel) {
            style = {
              verticalAlign: 'inherit',
              border: `1px solid #${color}`,
              borderRadius: '4px',
              backgroundColor: type === 'term' ? '#F2F4F7' : '#ffffff',
              padding: '0 8px',
              color: '#' + color
            };
          } else if (isTermWithInfo) {
            style = {
              verticalAlign: 'inherit',
              border: `1px solid #${color}`,
              borderRadius: '4px',
              padding: '0 8px'
            };
          } else {
            style = {
              backgroundImage: `url("${dragImage[type as keyof typeof dragImage].replaceAll('color', color)}")`,
              backgroundSize: '100% 100%',
              width: type === 'term' ? '66px' : '95px',
              height: '26px',
              position: 'relative',
              top: '2px'
            };
          }
          root.render(
            <span
              id={id}
              onClick={() => {
                onSelectionChange && onSelectionChange(infoObj);
              }}
              style={style}
              className={`${inputType === InputType.textarea ? 'block' : 'inline-block'} align-sub`}
            >
              {type === 'text' && label && (
                <span className="inline-block align-middle">
                  <MessageTextSquare02 size={16} color={`#${color}`} />
                </span>
              )}{' '}
              <span dangerouslySetInnerHTML={{ __html: (type === 'term' ? value : '') || label }}></span>
            </span>
          );
        }
      },
      minimap: {
        container: document.getElementsByClassName('minimap-container')[0] as any,
        extraClasses: 'default-font-size'
      }
    } as any)
      .then((editor: HtmlEditorType) => {
        setEditor(editor);
        if (type === 'preview') {
          editor.enableReadOnlyMode('');
          return;
        }
        const toolbarContainer = document.querySelector('.toolbar-container');
        editor.ui.view.toolbar.element && toolbarContainer?.appendChild(editor.ui.view.toolbar.element);
        editor.model.document.on('change:data', () => {
          const range = editor.model.createRangeIn(editor.model.document.getRoot()!);

          const fields: any[] = [];

          for (const item of range.getItems() as any) {
            if (item.name === 'signaturecomponent' && item.hasAttribute('info')) {
              const info: string = item.getAttribute('info') as string;
              if (info) {
                fields.push(JSON.parse(info));
              }
            }
          }

          onChange && onChange(handleHtml(editor.getData()), uniqBy(fields, 'id'));
        });
        editor.editing.view.document.on('selectionChange', () => {
          const dragimage = document.getElementById('dragimage-container');
          if (dragimage) {
            const color = dragimage.getAttribute('data-color');
            const type = dragimage.getAttribute('data-type') as keyof typeof dragImage;
            const id = dragimage.getAttribute('data-id');
            const custom = dragimage.getAttribute('data-custom');
            const value = dragimage.getAttribute('data-value');
            const required = dragimage.getAttribute('data-required');
            editor.editing.model.change((writer: any) => {
              editor.model.insertContent(
                writer.createElement('signaturecomponent', {
                  info: JSON.stringify({ type, color, id, custom, value, required })
                }),
                editor.model.document.selection.getFirstPosition()
              );
            });
            document.body.removeChild(dragimage as Node);
          }
        });
      })
      .catch((err: any) => {
        console.error(err);
      });
  }, [editor]);

  useEffect(() => {
    const targetNode = document.querySelector('.editor-content');
    const observerOptions = {
      childList: true // 观察目标子节点的变化，是否有添加或者删除
    };

    const observer = new MutationObserver((e) => {
      const res = e.find((item) => {
        return (item.removedNodes[0] as any)?.className === 'ck-fake-selection-container';
      });
      if (res) {
        onSelectionChange && onSelectionChange(undefined);
      }
    });
    observer.observe(targetNode!, observerOptions);
  }, []);

  return (
    <div className="document-editor">
      <div className="toolbar-container"></div>
      <div className="minimap-wrapper">
        <div className="editor-container">
          <div
            className="editor-content"
            ref={editorRef}
            onClick={(e) => {
              e.nativeEvent.stopImmediatePropagation();
            }}
          ></div>
        </div>
        <div className={`minimap-container ${type === 'preview' ? 'hidden' : ''}`}></div>
      </div>
    </div>
  );
});
